import { placeToCoordinates } from '@/views/tools/routing/Components/RoutingSwitch/Controllers/AxiosHelpers'
import { listAllDistricts } from '../district/district'
import { getAllBuildings } from '../building/building'
import apiClient from '@/services/axios'
import { STUDENT_STOP_PREFERENCE_OPTIONS } from '@/constants'

export async function createStudent(payload) {
  return apiClient.post('/students', payload)
}

// Bulk create for CSV/Excel uploads. One request per chunk (see
// BULK_UPLOAD_CHUNK_SIZE) so a large dump (300+) no longer trips the
// backend's 300-req/min rate limiter, and a single request stays under
// proxy idle timeouts. Backend returns
// { total, imported, results:[{ index, success, studentId, error }] }.
export async function bulkCreateStudents(students) {
  return apiClient.post('/students/bulk', { students })
}

// Rows per POST /students/bulk. Keeps each request under ALB/proxy idle
// timeouts while still far below the rate-limiter ceiling (one req per chunk).
const BULK_UPLOAD_CHUNK_SIZE = 75

export async function listAllStudents() {
  return apiClient.get('/students')
}

export async function getStudents(filter) {
  return apiClient.post('/students/searchStudents', filter)
}

export async function getStudentsByPaginate(query) {
  return apiClient.get('/students/paginate', {
    params: query,
  })
}

export async function getStudentsByBuilding(payload) {
  const query = {
    'filter.buildings.id': `$in:${payload.buildingIds.join(',')}`,
    limit: -1,
  }
  // Optional: omitting it returns every student of the building, which is
  // what the workspace map needs (Mercury routes all of them, regardless of
  // education type).
  if (payload.educationType) {
    query['filter.educationType'] = payload.educationType
  }
  // A routing group with sessions (e.g. "Madison PM PK") gets only their
  // students; a group without sessions gets the whole building.
  if (payload.routingGroupId) {
    query.routingGroupId = payload.routingGroupId
  }
  return apiClient.get(`/students/paginate`, {
    params: query,
  })
}

// `limit` defaults to the whole match list for name searches; callers that
// only need a first page (the wizard's empty-query default roster) pass a
// small number, since an empty search otherwise returns every student in the
// organization with all nine joined relations.
export async function getStudentsByName(payload, { limit = -1 } = {}) {
  const [firstName, lastName] = payload.split(' ')
  let query
  if (!lastName) {
    query = {
      'filter.firstName': `$sw:${firstName}`,
      limit,
    }
  } else {
    query = {
      'filter.firstName': `${firstName}`,
      'filter.lastName': `$sw:${lastName}`,
      limit,
    }
  }
  return apiClient.get(`/students/paginate`, {
    params: query,
  })
}

export async function getStudentById(id) {
  try {
    const response = await apiClient.get(`/students/${id}`)
    if (response.data) {
      return {
        success: true,
        status: 200,
        message: 'Student data',
        data: response.data,
      }
    } else {
      return {
        success: false,
        status: 404,
        data: {},
        message: `student with id: ${id} not found!`,
      }
    }
  } catch (error) {
    return {
      success: false,
      status: 500,
      message: 'Error fetching student by ID',
      error,
    }
  }
}

export async function updateStudent(id, studentData) {
  return await apiClient.patch(`/students/${id}`, studentData)
}

export async function resetAmPickupAddress(id) {
  return apiClient.post(`/students/${id}/reset-am-pickup-address`)
}

export async function resetPmDropoffAddress(id) {
  return apiClient.post(`/students/${id}/reset-pm-dropoff-address`)
}

export async function updateAffliatedAddress(id, address) {
  return apiClient.patch(`/students/affliated-addresses/${id}`, address)
}

export async function removeAffliatedAddress(id) {
  return apiClient.delete(`/students/affliated-addresses/${id}`)
}

export async function deleteStudent(id) {
  return apiClient.delete(`/students/${id}`)
}

export async function deleteStudents(ids) {
  return apiClient.delete('/students', { data: { ids } })
}

export async function bulkUpdateStudentGrade(ids, action) {
  return apiClient.patch('/students/bulk-grade', { ids, action })
}

function isValidDateString(str) {
  // Try creating a Date object from the string
  const date = new Date(str)

  // Check if the created Date object is valid and not NaN
  return date instanceof Date && !isNaN(date)
}

function convertDateString(numericalDate) {
  if (!numericalDate) return null

  if (typeof numericalDate === 'number') {
    // Convert numerical date to milliseconds since December 30, 1899 (UTC)
    const millisecondsSince1899 = numericalDate * 24 * 60 * 60 * 1000

    // Subtract 70 years and 1 day in milliseconds
    const millisecondsSince1970 =
      millisecondsSince1899 - (70 * 365 + 18) * 24 * 60 * 60 * 1000

    // Create a Date object using the adjusted milliseconds value
    const dateObject = new Date(millisecondsSince1970)

    // Get the components of the date (month, day, year)
    const month = dateObject.getMonth() + 1 // Month is zero-indexed
    const day = dateObject.getDate()
    const year = dateObject.getFullYear()

    // Format the date components into the desired string format "MM/DD/YYYY"
    const dateString = `${year}-${month}-${day}`

    return dateString
  }

  if (typeof numericalDate === 'string' && isValidDateString(numericalDate)) {
    return numericalDate.trim()
  } else {
    return null
  }
}

// Grades the backend accepts. "12+" is stored as "13" at payload-build time.
const VALID_GRADES = [
  'PK',
  'K',
  1,
  2,
  3,
  4,
  5,
  6,
  7,
  8,
  9,
  10,
  11,
  12,
  '12+',
].map((g) => g.toString())

function normStr(value) {
  return (value ?? '').toString().trim()
}

function hasValue(value) {
  return normStr(value) !== ''
}

// Files carry gender as "Male"/"Female"/"M"/"F" (any case); the app stores the
// canonical lowercase 'male'/'female'/'other' (stats query on those literals).
// Map to the canonical value; anything unrecognized (incl. blank) becomes 'other'
// so a routing-irrelevant field never holds a kid out of the system.
const GENDER_MAP = {
  m: 'male',
  male: 'male',
  f: 'female',
  female: 'female',
  o: 'other',
  other: 'other',
}
function normalizeGender(value) {
  return GENDER_MAP[normStr(value).toLowerCase()] || 'other'
}

// Stop preference: operators type the human label from the form dropdown, but
// the app stores internal values (Matt 7/30): `corner*` = "Corner Stop"
// (regular ed), `curb*` = "Curb Stop" (special ed — the old "House Stop" folds
// into "Curb"). Values migrated `house*` -> `curb*` 7/30 so value = label =
// meaning. Both the value set and the label lookup derive from the shared
// STUDENT_STOP_PREFERENCE_OPTIONS so the importer can't drift from the dropdown.
// Accept either the label or the raw stored value (any case); blank/unknown ->
// null (optional field).
const STOP_PREFERENCE_VALUES = new Set(
  STUDENT_STOP_PREFERENCE_OPTIONS.map((option) => option.value),
)
const STOP_PREFERENCE_LABEL_TO_VALUE = Object.fromEntries(
  STUDENT_STOP_PREFERENCE_OPTIONS.map((option) => [
    option.label.toLowerCase(),
    option.value,
  ]),
)
function normalizeStopPreference(value) {
  const raw = normStr(value)
  if (!raw) return null
  const lower = raw.toLowerCase()
  if (STOP_PREFERENCE_VALUES.has(lower)) return lower
  return STOP_PREFERENCE_LABEL_TO_VALUE[lower] || null
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Operators sometimes paste a whole address ("213 N. Lombard Rd, Addison, IL
// 60101") into the street column while city/state/zip ALSO have their own
// columns. Strip an exact trailing city/state/zip off the street so we store a
// clean street and don't duplicate the locality into the geocode query. Only
// strips a literal trailing match of each provided part; never empties the
// street (falls back to the original if it would).
function stripLocalityTail(street, city, state, zip) {
  let s = normStr(street)
  if (!s) return s
  for (const part of [zip, state, city]) {
    const val = normStr(part)
    if (!val) continue
    const re = new RegExp('[,\\s]*' + escapeRegExp(val) + '[,\\s]*$', 'i')
    const next = s.replace(re, '').trim()
    if (next) s = next
  }
  return s.replace(/[,\s]+$/, '').trim() || normStr(street)
}

// District match keys on ncesID AND districtName together (a name alone can be
// ambiguous), mirroring the original importer.
function findDistrict(allDistricts, element) {
  return allDistricts.find(
    (d) =>
      d.ncesID === normStr(element.ncesID) &&
      d.districtName === normStr(element.district),
  )
}

// Building match keys on name AND address together.
function findBuilding(allBuildings, name, address) {
  return allBuildings.find(
    (b) =>
      b.buildingName.trim() === normStr(name) &&
      b.address.trim() === normStr(address),
  )
}

// Validate a single parsed row against the system's districts/buildings.
// Returns { reasons: string[], badFields: string[] } - empty reasons means the
// row is importable. Exported so the inline fix-it popup can re-validate edits
// with the exact same rules as the initial upload. Note: address geocodability
// is NOT checked here (it's async) - that surfaces at payload-build time.
export function validateStudentRow(element, allDistricts, allBuildings) {
  const reasons = []
  const badFields = []

  if (!hasValue(element.firstName)) {
    reasons.push('First name is missing')
    badFields.push('firstName')
  }
  if (!hasValue(element.lastName)) {
    reasons.push('Last name is missing')
    badFields.push('lastName')
  }
  if (!hasValue(element.studentID)) {
    reasons.push('Student ID is missing')
    badFields.push('studentID')
  }

  if (!hasValue(element.grade)) {
    reasons.push('Grade is missing')
    badFields.push('grade')
  } else if (!VALID_GRADES.includes(normStr(element.grade))) {
    reasons.push(`Grade '${normStr(element.grade)}' is not one of PK, K, 1-12, 12+`)
    badFields.push('grade')
  }

  // Backend requires educationType (@IsNotEmpty). There is no safe neutral
  // default (unlike gender), so a blank one is held for the operator.
  if (!hasValue(element.education)) {
    reasons.push('Education type is missing')
    badFields.push('education')
  }

  const addrMissing = ['address', 'city', 'state', 'zip'].filter(
    (k) => !hasValue(element[k]),
  )
  if (addrMissing.length) {
    reasons.push('Home address is incomplete (needs address, city, state, and zip)')
    badFields.push(...addrMissing)
  }

  if (!hasValue(element.district) || !hasValue(element.ncesID)) {
    reasons.push('District is missing')
    badFields.push('district', 'ncesID')
  } else if (!findDistrict(allDistricts, element)) {
    reasons.push(
      `District '${normStr(element.district)}' is not in the system yet - add the district first, then re-upload`,
    )
    badFields.push('district', 'ncesID')
  }

  if (!hasValue(element.building1) || !hasValue(element.building1Address)) {
    reasons.push('No building assigned')
    badFields.push('building1', 'building1Address')
  } else if (
    !findBuilding(allBuildings, element.building1, element.building1Address)
  ) {
    reasons.push(
      `Building '${normStr(element.building1)}' is not in the system yet - add the building first, then re-upload`,
    )
    badFields.push('building1', 'building1Address')
  }

  // Optional second building: if either half is provided, both must be present
  // and must match a real building.
  if (hasValue(element.building2) || hasValue(element.building2Address)) {
    if (!hasValue(element.building2) || !hasValue(element.building2Address)) {
      reasons.push('Second building needs both a name and an address')
      badFields.push('building2', 'building2Address')
    } else if (
      !findBuilding(allBuildings, element.building2, element.building2Address)
    ) {
      reasons.push(
        `Building '${normStr(element.building2)}' is not in the system yet - add the building first, then re-upload`,
      )
      badFields.push('building2', 'building2Address')
    }
  }

  return { reasons, badFields: [...new Set(badFields)] }
}

// Build the create-student payload for a validated row. Geocodes the address;
// throws if it can't be located (caller turns that into a "needs you" row).
async function buildStudentPayload(element, allDistricts, allBuildings) {
  const district = findDistrict(allDistricts, element)
  const building1 = findBuilding(
    allBuildings,
    element.building1,
    element.building1Address,
  )
  const building2 =
    hasValue(element.building2) && hasValue(element.building2Address)
      ? findBuilding(allBuildings, element.building2, element.building2Address)
      : null

  const street = stripLocalityTail(
    element.address,
    element.city,
    element.state,
    element.zip,
  )
  const fullAddress =
    street +
    ', ' +
    normStr(element.city) +
    ', ' +
    normStr(element.state) +
    ', ' +
    normStr(element.zip)
  const response = await placeToCoordinates(fullAddress)
  const feature = response?.data?.features?.[0]
  if (!feature) {
    throw new Error(
      'Address could not be located on the map - please check the spelling',
    )
  }
  const long = feature.geometry.coordinates[0]
  const lat = feature.geometry.coordinates[1]

  const yesNo = (v) => normStr(v).toLowerCase() === 'yes'
  const requirement = {
    carSeat: yesNo(element.carSeat),
    boosterSeat: yesNo(element.boosterSeat),
    harness: yesNo(element.harness),
    houdiniHarness: yesNo(element.houdiniHarness),
    starSeat: yesNo(element.starSeat),
    airConditioning: yesNo(element.airConditioning),
    // Aide fields were renamed in the DB (Matron -> Driver Assistant, nurse ->
    // private_nurse, independant_aid -> independent_aide — see requirement.entity.ts),
    // so upload key == column key.
    driverAssistantShared: yesNo(element.driverAssistantShared),
    driverAssistantDedicated1: yesNo(element.driverAssistantDedicated1),
    driverAssistantDedicated2: yesNo(element.driverAssistantDedicated2),
    driverAssistantDedicated3: yesNo(element.driverAssistantDedicated3),
    privateNurse: yesNo(element.privateNurse),
    independentAide: yesNo(element.independentAide),
    epiPen: yesNo(element.epiPen),
    buckleGuard: yesNo(element.buckleGuard),
    fivePointHarness: yesNo(element.fivePointHarness),
    windowCushion: yesNo(element.windowCushion),
    zipperHarness: yesNo(element.zipperHarness),
    seizureProne: yesNo(element.seizureProne),
    wheelchair: yesNo(element.wheelchair),
  }

  return {
    firstName: capitalizeFirstLetter(normStr(element.firstName)),
    lastName: capitalizeFirstLetter(normStr(element.lastName)),
    nickName: element.nickName?.trim(),
    birthDate: convertDateString(element.birthDay),
    // Normalize to canonical male/female/other (handles "Male"/"M"/blank).
    gender: normalizeGender(element.gender),
    grade: normStr(element.grade) === '12+' ? '13' : element.grade,
    address: street,
    apartment: element.apartment,
    city: normStr(element.city),
    state: normStr(element.state),
    zip: element.zip + '',
    lat: lat,
    long: long,
    districtId: district.id,
    buildingIds: building2 ? [building1.id, building2.id] : [building1.id],
    studentId: element.studentID,
    activeDate: convertDateString(element.activeDate),
    terminateDate: convertDateString(element.terminateDate),
    active: element.active ? true : false,
    educationType: element.education?.trim(),
    householdId: element.householdID?.trim(),
    requirement: requirement,
    phoneNumber: element.phone,
    email: element.email,
    transportation: ['yes', 'true', '1'].includes(
      String(element.transportation ?? '')
        .trim()
        .toLowerCase(),
    ),
    stopPreference: normalizeStopPreference(element.stopPreference),
    weekdays: element.weekdays,
    notes: element.notes,
  }
}

// Resilient bulk importer. NEVER aborts the whole file: every row ends up in
// exactly one bucket - imported, skipped (already in the system), or
// needsAttention (missing/invalid data, returned for inline fixing). Also
// returns the district/building reference lists so the fix-it popup can offer
// exact-match dropdowns.
//
// Returns:
//   { status:200, total, imported, skipped:[{row,name,studentId}],
//     needsAttention:[{row,name,reasons:[],badFields:[],fields:{...}}],
//     districts, buildings }
// or { status:500, message } on an unexpected top-level failure.
//
// Optional onProgress({ phase, current, total, message }) for UI status
// during the multi-minute geocode + chunked-save wait.
export async function uploadExistingStudentData(data, onProgress) {
  const report = (phase, current, total, message) => {
    if (typeof onProgress === 'function') {
      onProgress({ phase, current, total, message })
    }
  }
  try {
    report(
      'prepare',
      0,
      data.length,
      'Loading districts, buildings, and existing students…',
    )
    const [districtResponse, buildingResponse, studentResponse] =
      await Promise.all([
        listAllDistricts(),
        getAllBuildings(),
        listAllStudents(),
      ])
    const allDistricts = districtResponse.data
    const allBuildings = buildingResponse.data
    const allStudents = studentResponse.data

    // Dedupe on ID + name (NOT ID alone): special-ed students often share a
    // placeholder ID like "0000", so ID-only keying would wrongly drop distinct
    // kids. A re-run then skips only the students already in the system.
    const dedupeKey = (id, first, last) =>
      `${normStr(id)}|${normStr(first).toLowerCase()}|${normStr(
        last,
      ).toLowerCase()}`
    const seenKeys = new Set(
      allStudents.map((st) =>
        dedupeKey(st.studentId, st.firstName, st.lastName),
      ),
    )

    const skipped = []
    const needsAttention = []
    // Rows that pass validation + geocoding, staged for chunked bulk requests.
    const toCreate = []
    let imported = 0

    for (let s = 0; s < data.length; s++) {
      report(
        'geocode',
        s + 1,
        data.length,
        `Checking addresses… ${s + 1} of ${data.length}`,
      )
      const element = data[s]
      const rowLabel =
        `${normStr(element.firstName)} ${normStr(element.lastName)}`.trim() ||
        `Row ${s + 1}`
      const key = dedupeKey(
        element.studentID,
        element.firstName,
        element.lastName,
      )
      if (seenKeys.has(key)) {
        skipped.push({ row: s + 1, name: rowLabel, studentId: element.studentID })
        continue
      }

      const { reasons, badFields } = validateStudentRow(
        element,
        allDistricts,
        allBuildings,
      )
      if (reasons.length) {
        needsAttention.push({
          row: s + 1,
          name: rowLabel,
          reasons,
          badFields,
          fields: { ...element },
        })
        continue
      }

      // Only reserve the dedupe key once we actually stage the row, so two
      // identical rows in the same file don't both import.
      seenKeys.add(key)
      try {
        const payload = await buildStudentPayload(
          element,
          allDistricts,
          allBuildings,
        )
        toCreate.push({
          row: s + 1,
          name: rowLabel,
          element: { ...element },
          payload,
        })
      } catch (error) {
        needsAttention.push({
          row: s + 1,
          name: rowLabel,
          reasons: [
            error?.message ||
              'Address could not be located on the map - please check the spelling',
          ],
          badFields: ['address', 'city', 'state', 'zip'],
          fields: { ...element },
        })
      }
    }

    // Chunked bulk requests for everything that passed local validation +
    // geocoding. Server-rejected rows come back per-index and are added to
    // needsAttention so the operator can fix them inline too.
    if (toCreate.length > 0) {
      const totalBatches = Math.ceil(toCreate.length / BULK_UPLOAD_CHUNK_SIZE)
      for (let offset = 0; offset < toCreate.length; offset += BULK_UPLOAD_CHUNK_SIZE) {
        const batchIndex = Math.floor(offset / BULK_UPLOAD_CHUNK_SIZE) + 1
        report(
          'save',
          batchIndex,
          totalBatches,
          `Saving… batch ${batchIndex} of ${totalBatches}`,
        )
        const chunk = toCreate.slice(offset, offset + BULK_UPLOAD_CHUNK_SIZE)
        try {
          const res = await bulkCreateStudents(chunk.map((t) => t.payload))
          const results = (res && res.data && res.data.results) || []
          for (const r of results) {
            const src = chunk[r.index]
            if (r.success) {
              imported++
            } else {
              needsAttention.push({
                row: src ? src.row : offset + r.index + 1,
                name: src ? src.name : `Row ${offset + r.index + 1}`,
                reasons: [r.error || 'Server rejected the row'],
                badFields: [],
                fields: src ? { ...src.element } : {},
              })
            }
          }
        } catch (error) {
          // Batch/transport failure (413, network, 5xx, proxy timeout). Not a
          // per-row field problem — hard-stop toast, no fake Ready modal.
          // Prior chunks may already have committed; say so honestly.
          const status = error.response && error.response.status
          const rawMsg =
            (error.response &&
              error.response.data &&
              error.response.data.message) ||
            error.message ||
            'Bulk upload request failed'
          const isPayloadTooLarge = status === 413
          const remaining = toCreate.length - offset
          let message
          if (imported > 0) {
            message = isPayloadTooLarge
              ? `Saved ${imported} students, then a chunk was too large to send (${remaining} left in this batch). Re-upload the file — already-added students will be skipped.`
              : `Saved ${imported} students, then the upload stopped (${rawMsg}). The remaining ${remaining} in this batch were not confirmed saved. Re-upload the file — already-added students will be skipped.`
          } else if (isPayloadTooLarge) {
            message = `This upload chunk is too large to send (${chunk.length} students). None of these were saved. Try a smaller file, or contact support if this keeps happening.`
          } else {
            message = `Could not reach the server to save these ${chunk.length} students. None of them were saved. Check your connection and try again. (${rawMsg})`
          }
          return {
            status: isPayloadTooLarge ? 413 : 502,
            message,
            total: data.length,
            imported,
            skipped,
            needsAttention: [],
            districts: allDistricts,
            buildings: allBuildings,
          }
        }
      }
    }

    return {
      status: 200,
      total: data.length,
      imported,
      skipped,
      needsAttention,
      districts: allDistricts,
      buildings: allBuildings,
    }
  } catch (error) {
    console.error(error)
    return {
      status: 500,
      message:
        error?.response?.data?.message || error?.message || 'Import failed',
    }
  }
}

function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}
